import { Injectable, inject } from '@angular/core';
import { BehaviorSubject, Observable, Subject, of } from 'rxjs';
import {
  catchError,
  distinctUntilChanged,
  filter,
  map,
  shareReplay,
  take,
  timeout,
} from 'rxjs/operators';
import { UIConfig } from '../../shared/models/ui-config.model';
import { TenantContextService } from '../tenant/tenant-context.service';
import { FilterFacade } from './filter.facade';
import { FilterStore } from './filter.store';
import {
  FilterLifecycleEvent,
  FilterLifecycleReason,
  FilterLifecycleState,
} from './filter-lifecycle.models';

@Injectable({ providedIn: 'root' })
export class FilterLifecycleFacade {
  private tenantContext = inject(TenantContextService);
  private filterFacade = inject(FilterFacade);
  private filterStore = inject(FilterStore);

  private stateSubject = new BehaviorSubject<FilterLifecycleState>({
    tenantKey: '',
    switchVersion: 0,
    switchId: 'tenant-switch-0',
    filterVersion: 0,
    schema: null,
    schemaReady: false,
    defaultsApplied: false,
    isReady: false,
  });

  private eventsSubject = new Subject<FilterLifecycleEvent>();

  readonly state$ = this.stateSubject.asObservable().pipe(
    distinctUntilChanged(
      (a, b) =>
        a.tenantKey === b.tenantKey &&
        a.switchVersion === b.switchVersion &&
        a.switchId === b.switchId &&
        a.filterVersion === b.filterVersion &&
        a.schema === b.schema &&
        a.schemaReady === b.schemaReady &&
        a.defaultsApplied === b.defaultsApplied &&
        a.isReady === b.isReady
    ),
    shareReplay({ bufferSize: 1, refCount: true })
  );

  readonly events$ = this.eventsSubject.asObservable();

  prepareForTenant(
    tenantKey: string,
    config: UIConfig,
    timeoutMs: number
  ): Observable<FilterLifecycleState> {
    const switchContext = this.tenantContext.getTenantSwitchContext();
    const filterVersion = this.stateSubject.value.filterVersion + 1;

    this.emitEvent(
      'config-ready',
      tenantKey,
      switchContext.version,
      switchContext.switchId,
      filterVersion
    );

    this.filterFacade.initWithUiConfig(tenantKey, config);
    this.publishState({
      tenantKey,
      switchVersion: switchContext.version,
      switchId: switchContext.switchId,
      filterVersion,
      schema: this.filterStore.schema(),
      schemaReady: true,
      defaultsApplied: false,
      isReady: false,
    });
    this.emitEvent(
      'schema-initialized',
      tenantKey,
      switchContext.version,
      switchContext.switchId,
      filterVersion
    );

    this.filterFacade.ensureDateRangeDefaults();
    this.publishState({
      ...this.stateSubject.value,
      defaultsApplied: true,
    });
    this.emitEvent(
      'defaults-applied',
      tenantKey,
      switchContext.version,
      switchContext.switchId,
      filterVersion
    );

    const buildReadyState = (): FilterLifecycleState => ({
      ...this.stateSubject.value,
      isReady: true,
    });
    const completeWithReady = (): void => {
      const readyState = buildReadyState();
      this.publishState(readyState);
      this.emitEvent(
        'filters-applied',
        tenantKey,
        switchContext.version,
        switchContext.switchId,
        filterVersion
      );
    };

    const snapshot = this.filterStore.getSelectionsSnapshot() as Record<string, unknown>;
    const facadeTenantKey = this.filterFacade.tenantKey();
    const dateRangeResolved = this.hasResolvedDateRange(snapshot);
    if (facadeTenantKey === tenantKey && dateRangeResolved) {
      completeWithReady();
      return of(buildReadyState());
    }

    return this.filterFacade.state$.pipe(
      filter(() => this.filterFacade.tenantKey() === tenantKey),
      filter((selections) => this.hasResolvedDateRange(selections as Record<string, unknown>)),
      take(1),
      timeout(timeoutMs),
      map(() => {
        completeWithReady();
        return buildReadyState();
      }),
      catchError((err) => {
        console.warn('FilterLifecycleFacade: prepareForTenant timed out or errored', {
          tenantKey,
          timeoutMs,
          error: err,
        });
        const fallbackState: FilterLifecycleState = {
          ...this.stateSubject.value,
          isReady: false,
        };
        this.publishState(fallbackState);
        return of(fallbackState);
      })
    );
  }

  private publishState(nextState: FilterLifecycleState): void {
    this.stateSubject.next(nextState);
  }

  private emitEvent(
    reason: FilterLifecycleReason,
    tenantKey: string,
    switchVersion: number,
    switchId: string,
    filterVersion: number
  ): void {
    this.eventsSubject.next({
      reason,
      tenantKey,
      switchVersion,
      switchId,
      filterVersion,
    });
  }

  private hasResolvedDateRange(selections: Record<string, unknown>): boolean {
    const dateRange = selections['date_range'] as
      | {
          type?: string;
          value?: {
            start?: string;
            end?: string;
          };
        }
      | undefined;

    return dateRange?.type === 'dateRange' && !!dateRange.value?.start && !!dateRange.value?.end;
  }
}
